import type { ReactNode } from "react";
import { FilterChips } from "@/components/filters/FilterChips";
import { AssigneeAvatar } from "@/domains/pm/components/shared/AssigneeAvatar";
import { resolveStatus } from "@/domains/pm/statuses/statusCatalogue";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import type { UserProfile } from "@/domains/pm/types";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { IssueFilterChip, IssueFilterDimension } from "./issueFilters";

interface IssueFilterChipsProps {
  chips: IssueFilterChip[];
  onOpenDimension: (dimension: IssueFilterDimension) => void;
  onClearAll: () => void;
  /** Resolves assignee ids to profile pictures in the "+N more" list. */
  users: UserProfile[];
  /** Picks the project's status colours. */
  buildingProjectId?: number | null;
  className?: string;
}

/** A status's colour dot. The label sits right beside it, so no tooltip. */
function StatusDot({
  status,
  buildingProjectId,
}: {
  status: string;
  buildingProjectId?: number | null;
}) {
  const { catalogue } = useStatusCatalogue();
  const config = resolveStatus(catalogue, buildingProjectId, status);
  return (
    <span
      role="img"
      aria-label={config.label}
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: config.dotColor }}
    />
  );
}

/**
 * The issues toolbar's chips: the shared `FilterChips` with the PM copy, and
 * status colours and profile pictures beside the values in the "+N more" list.
 */
export function IssueFilterChips({ users, buildingProjectId, ...props }: IssueFilterChipsProps) {
  const { t } = useLanguage();
  const f = t.pm.issues.filterBy;
  const valueIcon = (dimension: IssueFilterDimension, id: string): ReactNode => {
    if (dimension === "status") {
      return <StatusDot status={id} buildingProjectId={buildingProjectId} />;
    }
    if (dimension === "assignee") {
      const user = users.find((u) => String(u.id) === id);
      return user ? <AssigneeAvatar user={user} size={16} /> : null;
    }
    return null;
  };
  return (
    <FilterChips
      {...props}
      removeLabel={f.remove}
      clearAllLabel={f.clearAll}
      moreLabel={(count) => f.moreFilters.replace("{count}", String(count))}
      valueIcon={valueIcon}
    />
  );
}
